import { normalizeDisplayLinesForRender } from '@/utils/math-live'
import { stripIncludegraphicsAlt } from '@/utils/mathpix-figure'
import { unwrapStrayItemStems } from '@/utils/mathpix-item-stem'
// Custom style to fix spacing issues and limit image width
export const mathpixStyles = `
	/* Global container settings */
	.mathpix-outer-container {
		font-size: 1rem;
		line-height: 1.5;
		color: #333;
	}
	
	/* Image handling */
	.mathpix-markdown img {
		max-width: 95% !important;
		height: auto !important;
		margin: 1em auto !important;
		display: block !important;
		box-shadow: 0 2px 8px rgba(0,0,0,0.1);
		border-radius: 4px;
	}
	
	/* Additional styles for direct rendering (fallback mode) */
	.mathpix-markdown-direct .katex {
		text-align: center !important;
		margin: 1em auto !important;
	}
	
	.mathpix-markdown-direct p {
		margin: 0.75em 0 !important;
		line-height: 1.5 !important;
	}
	
	.mathpix-markdown-direct img {
		max-width: 95% !important;
		height: auto !important;
		margin: 1em auto !important;
		display: block !important;
	}
	
	/* Reset base paragraph margins for better control */
	.mathpix-markdown p {
		margin: 0.75em 0 !important;
		line-height: 1.5 !important;
		text-align: left !important;
	}
	
	/* Ensure proper display of paragraph breaks */
	.mathpix-markdown p:empty {
		display: none !important;
	}
	
	/* Better spacing for equations */
	.mathpix-markdown .math-display {
		margin: 1em 0 !important;
		padding: 0.5em 0 !important;
		overflow-x: auto !important;
		text-align: center !important;
	}
	
	/* Keep inline math properly aligned */
	.mathpix-markdown .math-inline {
		margin: 0 0.1em !important;
		vertical-align: -10% !important;
	}
	
	/* Improve list spacing */
	.mathpix-markdown ul, .mathpix-markdown ol {
		margin: 0.7em 0 !important;
		padding-left: 1.5em !important;
	}
	
	.mathpix-markdown li {
		margin-bottom: 0.3em !important;
	}
	
	/* Better spacing for headers */
	.mathpix-markdown h1, .mathpix-markdown h2, .mathpix-markdown h3, 
	.mathpix-markdown h4, .mathpix-markdown h5, .mathpix-markdown h6 {
		margin-top: 1.25em !important;
		margin-bottom: 0.75em !important;
		font-weight: 600 !important;
	}
	
	/* Better handling of multi-line equations */
	.mathpix-markdown .math-display .katex-display {
		overflow-x: auto !important;
		overflow-y: hidden !important;
		padding: 0.25em 0 !important;
	}
	
	/* Better figure rendering and spacing */
	.mathpix-markdown figure {
		margin: 1.5em auto !important;
		text-align: center !important;
		max-width: 95% !important;
	}
	
	.mathpix-markdown figcaption {
		font-style: italic !important;
		font-size: 0.9em !important;
		text-align: center !important;
		margin-top: 0.5em !important;
		color: #555 !important;
	}
	
	/* Handle figure styling */
	.mathpix-markdown p + figure {
		margin-top: 0.5em !important;
	}
	
	/* Style emphasis and strong text */
	.mathpix-markdown em {
		font-style: italic !important;
	}
	
	.mathpix-markdown strong {
		font-weight: 600 !important;
		display: inline-block !important;
	}
	
	/* Fix table display */
	.mathpix-markdown table {
		margin: 1em 0 !important;
		border-collapse: collapse !important;
		width: auto !important;
	}
	
	.mathpix-markdown td, .mathpix-markdown th {
		padding: 0.5em !important;
		border: 1px solid #ddd !important;
	}
	
	/* Better display for math containers */
	.mjx-container {
		overflow-x: auto !important;
		padding-bottom: 0.15em !important;
	}
	
	/* Center displayed equations */
	.mjx-container[display="true"] {
		display: block !important;
		margin: 1em auto !important;
		text-align: center !important;
	}
	
	/* Style for MathJax */
	.MathJax {
		text-align: center !important;
		margin: 0 auto !important;
	}
	
	/* Custom classes for special elements */
	.figure-text {
		text-align: center !important;
		font-weight: bold !important;
		margin: 1.2em 0 0.3em 0 !important;
	}
	
	.parametric-equations {
		text-align: center !important;
		margin: 1em 0 !important;
	}
	
	.question-part {
		margin-top: 1.2em !important;
		font-weight: 500 !important;
		padding-left: 0.5em !important;
	}
	
	.equation-container {
		overflow-x: auto !important;
		margin: 1em 0 !important;
		text-align: center !important;
		width: 100% !important;
	}
	
	/* Specifically style the figure label */
	.figure-number {
		font-weight: bold !important;
	}
	
	/* Style the curve C and other math variables in text */
	.math-variable {
		font-style: italic !important;
		font-weight: normal !important;
	}
	
	/* Additional styles for parametric equations */
	.parametric-block {
		display: flex !important;
		flex-direction: column !important;
		align-items: center !important;
		margin: 1em 0 !important;
	}
	
	/* Ensure all equations are centered properly */
	.mathpix-markdown .katex-display {
		text-align: center !important;
		width: 100% !important;
	}
`

// Helper function to calculate adaptive interval based on processing status and document size
export function calculateMathpixProcessingInterval(
  status: string,
  percent: number,
  pageCount: number,
  bufferSize: number,
): number {
  // Estimate page count if not directly provided
  const estimatedPageCount = pageCount || Math.ceil(bufferSize / 500000)

  // Base interval calculations
  const baseInterval = Math.min(2000 + estimatedPageCount * 250, 10000) // 2s base + 250ms per page, max 10s

  // Adjust interval based on status
  if (status === 'completed') {
    return 1000 // Quick check when completed
  } else if (percent > 80) {
    return Math.max(baseInterval * 0.5, 1000) // Faster checks as we approach completion
  } else if (percent > 50) {
    return Math.max(baseInterval * 0.75, 1500) // Slightly faster as we pass halfway
  } else if (percent < 10) {
    return Math.min(baseInterval * 1.5, 15000) // Longer at the beginning
  }

  return baseInterval
}

/**
 * Normalizes LaTeX quirks that mathpix-markdown-it cannot parse.
 *
 * `\begin{tabular}[t]{...}` (optional position argument) is valid LaTeX, but the
 * mathpix-markdown-it table parser only recognizes `\begin{tabular}{...}` — with the
 * argument present the whole table falls through and renders as raw source text.
 * GPT-5.6 extraction emits the `[t]` variant (gpt-4o did not), so sanitize at render
 * time to cover both newly ingested and already stored content. `\multirow[t]` is
 * left untouched — the parser handles it fine inside a recognized table.
 */
export const sanitizeMathpixLatex = (content: string): string => {
  if (!content) return content

  // KaTeX has no \displaylines macro (MathLive stores multi-line typed
  // working wrapped in it) - unwrap to aligned so stored working and its
  // line annotations render instead of leaking the raw macro.
  // Uploaded-paper stems stored as `\item[n] ...` outside a list render as an
  // empty <li>, hiding the question text - unwrap them to plain paragraphs.
  // Mathpix figures carry `alt={}` in their options, which the parser cannot
  // read - the image showed as raw `,max width=...]{url}` text.
  return stripIncludegraphicsAlt(
    unwrapStrayItemStems(normalizeDisplayLinesForRender(content)),
  ).replace(/\\begin\{tabular\}\[[a-z]+\]/g, '\\begin{tabular}')
}

// Regex for the ![](url) pattern.
// [^)]+ matches any characters except the closing parenthesis.
export const mathpixImageLinkRegex: RegExp = /!\[\]\((https?:\/\/[^)]+)\)/g

/**
 * Extracts all image URLs from a Mathpix/Markdown formatted string.
 * @param content The string containing the content to parse.
 * @returns An array of found image URLs.
 */
export const extractMathpixImageLinks = (content: string): string[] => {
  // Regex looks for the ![](url) pattern.
  const links: string[] = []
  let match

  while ((match = mathpixImageLinkRegex.exec(content)) !== null) {
    // match[1] is the capture group containing only the URL itself.
    links.push(match[1])
  }

  return links
}
